/**
 * In-memory logger for the add-in. Forwards to console and buffers entries
 * so DebugLogPanel can show them in the task pane when debug mode is on.
 */
export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

export interface LogEntry {
  time: string;
  level: LogLevel;
  message: string;
  detail?: string;
}

const MAX_ENTRIES = 200;
const buffer: LogEntry[] = [];
let listeners: Array<() => void> = [];

function emit() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (_) {
      // ignore
    }
  });
}

function add(level: LogLevel, message: string, detail?: string) {
  const entry: LogEntry = {
    time: new Date().toISOString().split('T')[1].slice(0, 12),
    level,
    message,
    detail: detail ?? undefined,
  };
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer.shift();

  const cons = typeof console !== 'undefined' ? console : null;
  const prefix = `[AOA ${entry.time}]`;
  if (cons) {
    if (level === 'error') cons.error(prefix, message, detail ?? '');
    else if (level === 'warn') cons.warn(prefix, message, detail ?? '');
    else cons.log(prefix, message, detail ?? '');
  }

  emit();
}

/** Format a single log entry as plain text (for copy to clipboard). */
function formatEntry(e: LogEntry): string {
  const line = `${e.time} [${e.level}] ${e.message}`;
  return e.detail != null && e.detail !== '' ? `${line}\n  ${e.detail}` : line;
}

export const addinLogger = {
  info: (message: string, detail?: string) => add('info', message, detail),
  warn: (message: string, detail?: string) => add('warn', message, detail),
  error: (message: string, detail?: string) => add('error', message, detail),
  debug: (message: string, detail?: string) => add('debug', message, detail),

  getEntries: (): LogEntry[] => [...buffer],
  /** Plain-text representation of all entries for copy/paste. */
  getEntriesAsText: (): string => buffer.map(formatEntry).join('\n'),

  clear: () => {
    buffer.length = 0;
    emit();
  },
  subscribe: (fn: () => void) => {
    listeners.push(fn);
    return () => {
      listeners = listeners.filter((l) => l !== fn);
    };
  },

  /**
   * Log Identity/context diagnostics to help troubleshoot "Identity API is not supported".
   * Call once after Office.onReady.
   */
  logIdentityDiagnostics: () => {
    try {
      const ctx = (typeof Office !== 'undefined' && Office?.context) as unknown as { auth?: unknown; manifest?: { id?: string }; diagnostics?: { hostVersion?: string; platform?: string } } | undefined;
      const hasAuth = Boolean(ctx?.auth);
      addinLogger.debug('Identity diagnostics: Office.context.auth present', String(hasAuth));
      if (ctx?.manifest?.id) {
        addinLogger.debug('Identity diagnostics: manifest id', ctx.manifest.id);
      }
      const diag = ctx?.diagnostics;
      if (diag && (diag.hostVersion || diag.platform)) {
        addinLogger.debug(
          'Identity diagnostics: host',
          [diag.platform, diag.hostVersion].filter(Boolean).join(' ')
        );
      }
      // Office SSO availability
      const ssoAvailable = Boolean(
        typeof Office !== 'undefined' && (Office as { auth?: { getAccessToken?: unknown } }).auth?.getAccessToken
      );
      addinLogger.debug('Identity diagnostics: Office SSO available', String(ssoAvailable));
    } catch (err) {
      addinLogger.warn('Identity diagnostics: failed to read context', String(err));
    }
  },
};

/**
 * Default debug off for production. Enable via ?debug=1 or localStorage aoa-debug=true for troubleshooting.
 */
const DEFAULT_DEBUG_ENABLED = false;

/** Debug mode: show in-pane log. Default on for troubleshooting; disable via ?debug=0 or localStorage aoa-debug=false */
export function isDebugMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === '0' || params.get('debug') === 'false') return false;
    if (params.get('debug') === '1' || params.get('debug') === 'true') return true;
    const stored = localStorage.getItem('aoa-debug');
    if (stored === 'false') return false;
    if (stored === 'true') return true;
    return DEFAULT_DEBUG_ENABLED;
  } catch {
    return DEFAULT_DEBUG_ENABLED;
  }
}

export function setDebugMode(enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem('aoa-debug', 'true');
    else localStorage.setItem('aoa-debug', 'false');
  } catch (_) {
    // ignore
  }
}
